/**
 * Strip HTML tags + decode HTML entities for plain-text rendering.
 *
 * Used everywhere we display content that was authored as HTML (insights,
 * descriptions, dataleo_angle…) but the surrounding UI needs a clean
 * plain-text preview — line-clamped cards, meta descriptions, JSON-LD,
 * OG/Twitter cards, RSS, etc.
 *
 * "S&amp;OP" → "S&OP",  "<p>Foo</p>" → "Foo",  "Bar &nbsp; baz" → "Bar baz"
 */
export const decodeHtmlEntities = (s) => {
  if (s == null) return "";
  const str = String(s);
  if (str.indexOf("&") === -1) return str;
  if (typeof document !== "undefined") {
    const t = document.createElement("textarea");
    t.innerHTML = str;
    return t.value;
  }
  // SSR / Node fallback — covers the most common entities. Used by the
  // Express SSR layer and any preview-rendering tools that don't have DOM.
  return str
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&rsquo;/g, "\u2019")
    .replace(/&lsquo;/g, "\u2018")
    .replace(/&laquo;/g, "\u00AB")
    .replace(/&raquo;/g, "\u00BB")
    .replace(/&hellip;/g, "\u2026")
    .replace(/&mdash;/g, "\u2014")
    .replace(/&ndash;/g, "\u2013")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, n) => String.fromCharCode(parseInt(n, 16)));
};

/**
 * Convert HTML to a clean plain-text preview: strip tags, decode entities,
 * collapse whitespace, optionally clamp to a max length.
 */
export const htmlToPlain = (s, max = 0) => {
  const stripped = String(s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const decoded = decodeHtmlEntities(stripped);
  if (max > 0 && decoded.length > max) return decoded.slice(0, max - 1).trimEnd() + "\u2026";
  return decoded;
};
